import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Film, Wand2, Search, Link2, X } from 'lucide-react';
import type { Clip, VfxShot, VfxShotClipRole } from '@/types/database';

interface LinkRelationshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceType: 'clip' | 'vfx_shot';
  sourceId: string;
  sourceName: string;
  clips: Clip[];
  vfxShots: VfxShot[];
  allTags: string[];
  onLinkClip: (targetClipId: string, relationshipType?: string) => Promise<void>;
  onLinkVfxShot: (targetVfxShotId: string, relationshipType?: VfxShotClipRole) => Promise<void>;
}

type EntityType = 'clips' | 'vfx_shots' | 'all';
type ClipRelationshipType = 'related' | 'source' | 'version' | 'reference' | 'storyboard';

const CLIP_RELATIONSHIP_OPTIONS: { value: ClipRelationshipType; label: string }[] = [
  { value: 'related', label: 'Related' },
  { value: 'source', label: 'Source Material' },
  { value: 'version', label: 'Version' },
  { value: 'reference', label: 'Reference' },
  { value: 'storyboard', label: 'Storyboard' },
];

const VFX_SHOT_RELATIONSHIP_OPTIONS: { value: VfxShotClipRole; label: string; description: string }[] = [
  { value: 'source', label: 'Source', description: 'Original source clip from timeline' },
  { value: 'plate', label: 'Plate', description: 'Background plate for VFX composition' },
  { value: 'comp', label: 'Comp', description: 'Composite/comp clip' },
  { value: 'render', label: 'Render', description: 'VFX render output' },
  { value: 'final', label: 'Final', description: 'Final rendered output' },
  { value: 'reference', label: 'Reference', description: 'Reference footage' },
  { value: 'matte', label: 'Matte', description: 'Matte clip' },
  { value: 'clean_plate', label: 'Clean Plate', description: 'Clean plate without actors/objects' },
];

export function LinkRelationshipDialog({
  open,
  onOpenChange,
  sourceType,
  sourceId,
  sourceName,
  clips,
  vfxShots,
  allTags,
  onLinkClip,
  onLinkVfxShot,
}: LinkRelationshipDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [clipRelationshipType, setClipRelationshipType] = useState<ClipRelationshipType>('related');
  const [vfxShotRelationshipType, setVfxShotRelationshipType] = useState<VfxShotClipRole>('source');
  const [isLinking, setIsLinking] = useState(false);

  // Filter and search logic
  const filteredItems = useMemo(() => {
    let items: Array<{ type: 'clip' | 'vfx_shot'; id: string; name: string; tags: string[]; metadata?: any }> = [];

    // Add clips
    if (selectedEntityType === 'all' || selectedEntityType === 'clips') {
      items.push(
        ...clips
          .filter((clip) => clip.id !== sourceId) // Don't show the source item
          .map((clip) => ({
            type: 'clip' as const,
            id: clip.id,
            name: clip.name,
            tags: clip.tags || [],
            metadata: clip.custom_metadata,
          }))
      );
    }

    // Add VFX shots
    if (selectedEntityType === 'all' || selectedEntityType === 'vfx_shots') {
      items.push(
        ...vfxShots
          .filter((shot) => shot.id !== sourceId) // Don't show the source item
          .map((shot) => ({
            type: 'vfx_shot' as const,
            id: shot.id,
            name: shot.shot_name,
            tags: shot.tags || [],
            metadata: shot.custom_metadata,
          }))
      );
    }

    // Filter by tag
    if (selectedTag !== 'all') {
      items = items.filter((item) => item.tags.includes(selectedTag));
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(query);
        const tagMatch = item.tags.some((tag) => tag.toLowerCase().includes(query));
        const metadataMatch = item.metadata
          ? Object.values(item.metadata).some((val) => String(val).toLowerCase().includes(query))
          : false;
        return nameMatch || tagMatch || metadataMatch;
      });
    }

    return items;
  }, [clips, vfxShots, selectedEntityType, selectedTag, searchQuery, sourceId]);

  const handleLink = async (targetType: 'clip' | 'vfx_shot', targetId: string) => {
    setIsLinking(true);
    try {
      if (targetType === 'clip') {
        await onLinkClip(targetId, clipRelationshipType);
      } else {
        await onLinkVfxShot(targetId, vfxShotRelationshipType);
      }
      onOpenChange(false);
      // Reset state
      setSearchQuery('');
      setSelectedTag('all');
      setClipRelationshipType('related');
      setVfxShotRelationshipType('source');
    } catch (error) {
      console.error('Failed to create link:', error);
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state
    setSearchQuery('');
    setSelectedTag('all');
    setSelectedEntityType('all');
    setClipRelationshipType('related');
    setVfxShotRelationshipType('source');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Link Relationship</DialogTitle>
          <DialogDescription>
            Link <span className="font-medium text-foreground">{sourceName}</span> to another clip or VFX shot
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 min-h-0 flex flex-col">
          {/* Search and Filters */}
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, tags, or metadata..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Entity Type Toggle */}
            <Tabs value={selectedEntityType} onValueChange={(v) => setSelectedEntityType(v as EntityType)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="clips">Clips</TabsTrigger>
                <TabsTrigger value="vfx_shots">VFX Shots</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Filters Row */}
            <div className="flex gap-2">
              {/* Tag Filter */}
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Clip Relationship Type (for clip-to-clip linking) */}
              {sourceType === 'clip' && selectedEntityType !== 'vfx_shots' && (
                <Select value={clipRelationshipType} onValueChange={(v) => setClipRelationshipType(v as ClipRelationshipType)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Clip relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIP_RELATIONSHIP_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* VFX Shot Relationship Type (for clip-to-vfx_shot OR vfx_shot-to-clip linking) */}
              {((sourceType === 'clip' && selectedEntityType !== 'clips') || (sourceType === 'vfx_shot' && selectedEntityType !== 'vfx_shots')) && (
                <Select value={vfxShotRelationshipType} onValueChange={(v) => setVfxShotRelationshipType(v as VfxShotClipRole)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue>
                      {VFX_SHOT_RELATIONSHIP_OPTIONS.find(opt => opt.value === vfxShotRelationshipType)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {VFX_SHOT_RELATIONSHIP_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 min-h-0 border rounded-lg overflow-auto">
            <div className="p-2 space-y-1">
                {filteredItems.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No items found</p>
                    <p className="text-sm mt-1">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  filteredItems.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {item.type === 'clip' ? (
                          <Film className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <Wand2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.name}</p>
                          {item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {item.tags.length > 3 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{item.tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleLink(item.type, item.id)}
                        disabled={isLinking}
                        className="flex-shrink-0"
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Link
                      </Button>
                    </div>
                  ))
                )}
              </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
